import { lazy, Suspense, useMemo } from 'react';
import { createBrowserRouter, Link, Navigate, Outlet, useLocation } from 'react-router';
import { useAuth } from './context/AuthContext';
import {
  FeatureConfigProvider,
  useFeatureConfig,
} from './context/FeatureConfigContext';
import { useIsMobile } from './hooks/useIsMobile';
import Header from './components/Header';
import Footer from './components/Footer';
import MobileAppHeader from './components/MobileAppHeader';
import { WasmProvider } from './context/WasmContext';
import { buildLoginPath } from './utils/authRedirect';

const allowAllFrequencies = () => true;

function SimulatorFeatureConfig({ children }) {
  const parent = useFeatureConfig();
  const value = useMemo(
    () => ({
      ...parent,
      isFrequencyPlayable: allowAllFrequencies,
      isFrequencyInteractable: allowAllFrequencies,
    }),
    [parent]
  );

  return <FeatureConfigProvider value={value}>{children}</FeatureConfigProvider>;
}

// Eagerly loaded — needed for landing page
import Landing from './pages/Landing';

// Lazy loaded — fetched only when their routes are visited
const Game = lazy(() => import('./pages/Game'));
const MobileGame = lazy(() => import('./pages/MobileGame'));
const Simulator = lazy(() => import('./pages/Simulator'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const Welcome = lazy(() => import('./pages/Welcome'));
const MobileAuth = lazy(() => import('./pages/MobileAuth'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const VerifyEmailChange = lazy(() => import('./pages/VerifyEmailChange'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const About = lazy(() => import('./pages/About'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const DataDeletion = lazy(() => import('./pages/DataDeletion'));
const Newsletter = lazy(() => import('./pages/Newsletter'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Profile = lazy(() => import('./pages/Profile'));

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="loading" role="status" aria-live="polite">
        <span className="visually-hidden">Checking authentication...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={buildLoginPath(location.pathname, location.search)} replace />;
  }

  return children;
}

// Layout for auth pages (no site header; a single escape back to landing)
function AuthLayout() {
  return (
    <>
      <nav className="auth-layout-nav" aria-label="Page">
        <Link to="/" className="auth-back-link">
          Back to EQ Your Ears
        </Link>
      </nav>
      <Outlet />
    </>
  );
}

// Layout for main app pages (with header and footer)
function AppLayout() {
  const { pathname } = useLocation();
  const isSitePage = pathname === '/' || pathname === '/about' || pathname === '/newsletter' || 
                     pathname === '/privacy-policy' || pathname === '/terms-of-service' || 
                     pathname === '/cookie-policy' || pathname === '/data-deletion';
  return (
    <div className={`app-container${isSitePage ? ' app-container--landing' : ''}`}>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <Header />
      <main id="main-content" className="main-content" tabIndex={-1}>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

// Auth route — renders MobileAuth on mobile, desktop Login/Signup on larger viewports.
// `desktopElement` is the desktop component to render, `initialView` is the view
// MobileAuth should open in ('login' or 'signup').
function AuthRoute({ desktopElement, initialView }) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <MobileAuth initialView={initialView} />;
  }

  return desktopElement;
}

// Game route — renders MobileGame on mobile, Game on desktop (with app layout)
function GameRoute() {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <>
        <WasmProvider>
          <MobileGame />
        </WasmProvider>
        <Footer />
      </>
    );
  }

  return (
    <div className="app-container">
      <a href="#start-btn" className="skip-link" onClick={(e) => {
        e.preventDefault();
        document.getElementById('start-btn')?.focus();
      }}>
        Skip to main content
      </a>
      <Header />
      <main id="main-content" className="main-content">
        <WasmProvider>
          <Game />
        </WasmProvider>
      </main>
      <Footer />
    </div>
  );
}

function SimulatorRoute() {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <>
        <MobileAppHeader />
        <WasmProvider>
          <SimulatorFeatureConfig>
            <Simulator />
          </SimulatorFeatureConfig>
        </WasmProvider>
        <Footer />
      </>
    );
  }

  return (
    <div className="app-container">
      <a href="#start-btn" className="skip-link" onClick={(event) => {
        event.preventDefault();
        document.getElementById('start-btn')?.focus();
      }}>
        Skip to simulator controls
      </a>
      <Header />
      <main id="main-content" className="main-content">
        <WasmProvider>
          <SimulatorFeatureConfig>
            <Simulator />
          </SimulatorFeatureConfig>
        </WasmProvider>
      </main>
      <Footer />
    </div>
  );
}

// Root layout that wraps everything
function RootLayout() {
  return (
    <Suspense fallback={
      <div className="loading" role="status" aria-live="polite">
        <span className="visually-hidden">Loading...</span>
      </div>
    }>
      <Outlet />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Auth routes (no header)
      {
        element: <AuthLayout />,
        children: [
          { path: '/login', element: <AuthRoute desktopElement={<Login />} initialView="login" /> },
          { path: '/signup', element: <AuthRoute desktopElement={<Signup />} initialView="signup" /> },
          { path: '/welcome', element: <Welcome /> },
          { path: '/verify-email', element: <VerifyEmail /> },
          { path: '/verify-email-change', element: <VerifyEmailChange /> },
          { path: '/forgot-password', element: <ForgotPassword /> },
          { path: '/reset-password', element: <ResetPassword /> },
        ],
      },
      // Game route (handles its own layout — MobileGame on mobile, desktop app layout on desktop)
      { path: '/app', element: <GameRoute /> },
      { path: '/simulator', element: <SimulatorRoute /> },
      // Main app routes (with header and footer)
      {
        element: <AppLayout />,
        children: [
          { path: '/', element: <Landing /> },
          {
            path: '/dashboard',
            element: (
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            ),
          },
          {
            path: '/profile',
            element: (
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            ),
          },
          // Legal and informational pages
          { path: '/about', element: <About /> },
          { path: '/newsletter', element: <Newsletter /> },
          { path: '/privacy-policy', element: <PrivacyPolicy /> },
          { path: '/terms-of-service', element: <TermsOfService /> },
          { path: '/cookie-policy', element: <CookiePolicy /> },
          { path: '/data-deletion', element: <DataDeletion /> },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
